import {SmokingSessionStatus} from './session.js';

function resultStatus(isOnline){
  return isOnline()?'saved_pending_sync':'saved_offline';
}

function preLive(status){
  return ['draft','planned','preparation','ready'].includes(String(status||'').toLowerCase());
}

export function minimumPlannedSessionRequirements({items=[],stages=[]}={}){
  const missing=[];
  if(!(items||[]).length)missing.push('items');
  if(!(stages||[]).length)missing.push('stages');
  return Object.freeze({
    valid:missing.length===0,
    missing:Object.freeze(missing),
    itemCount:(items||[]).length,
    stageCount:(stages||[]).length
  });
}

export function createSmokingSessionEditor({
  sessionStore,
  itemStore,
  stageStore,
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(!sessionStore?.createDraft||!sessionStore?.resumeDraft||!sessionStore?.get||!sessionStore?.update
    ||!sessionStore?.startSmoking||!sessionStore?.transition||!sessionStore?.softDelete){
    throw new TypeError('Smoking session store is required');
  }
  if(!itemStore?.list)throw new TypeError('Smoking item store is required');
  if(!stageStore?.list)throw new TypeError('Smoking stage store is required');

  let current=null;

  function snapshot(){
    return Object.freeze({
      session:current,
      sessionId:current?.id||null,
      status:current?.status||null,
      editable:Boolean(current&&preLive(current.status)),
      autosave:true,
      offline:!isOnline()
    });
  }

  async function create(input={}){
    current=await sessionStore.createDraft(input);
    return snapshot();
  }

  async function resume(){
    current=await sessionStore.resumeDraft();
    return snapshot();
  }

  async function resumeOrCreate(input={}){
    current=await sessionStore.resumeDraft();
    if(!current)current=await sessionStore.createDraft(input);
    return snapshot();
  }

  async function open(id){
    current=await sessionStore.get(id);
    if(!current)throw new Error('SMOKING_SESSION_NOT_FOUND');
    return snapshot();
  }

  async function autosave(changes={}){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    if(!preLive(current.status))throw new Error('SMOKING_SESSION_LOCKED_AFTER_START');
    current=await sessionStore.update(current.id,changes);
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  async function setSmoker(input){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    current=await sessionStore.setSmoker(current.id,input);
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  async function clearSmoker(){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    current=await sessionStore.clearSmoker(current.id);
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  async function plannedReadiness(){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    const [items,stages]=await Promise.all([
      itemStore.list(current.id),
      stageStore.list(current.id)
    ]);
    return minimumPlannedSessionRequirements({items,stages});
  }

  async function saveAsPlanned(){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    if(current.status===SmokingSessionStatus.PLANNED){
      return Object.freeze({ok:true,status:'already_planned',state:snapshot()});
    }
    if(current.status!==SmokingSessionStatus.DRAFT){
      throw new Error('ONLY_DRAFT_CAN_BE_PLANNED');
    }
    const readiness=await plannedReadiness();
    if(!readiness.valid){
      return Object.freeze({
        ok:false,
        status:'validation_error',
        retryable:false,
        missing:readiness.missing,
        state:snapshot()
      });
    }
    current=await sessionStore.transition(current.id,SmokingSessionStatus.PLANNED);
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  async function startSmoking(stageId,{at}={}){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    current=await sessionStore.startSmoking(current.id,{stageId,at});
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  async function requestFinish(){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    if(current.status!==SmokingSessionStatus.SMOKING){
      throw new Error('SMOKING_SESSION_FINISH_REQUIRES_SMOKING');
    }
    return Object.freeze({
      ok:false,
      status:'confirmation_required',
      action:'finish',
      sessionId:current.id,
      activeStageId:current.activeStageId
    });
  }

  async function finish({confirmed=false,at}={}){
    const plan=await requestFinish();
    if(!confirmed)return plan;
    current=await sessionStore.transition(current.id,SmokingSessionStatus.COMPLETED,{at});
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  async function requestCancel(){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    if(!['draft','planned','preparation','ready'].includes(current.status)){
      throw new Error('SMOKING_SESSION_LOCKED_AFTER_START');
    }
    return Object.freeze({
      ok:false,
      status:'confirmation_required',
      action:'cancel',
      sessionId:current.id
    });
  }

  async function cancel({confirmed=false}={}){
    const plan=await requestCancel();
    if(!confirmed)return plan;
    current=await sessionStore.transition(current.id,SmokingSessionStatus.CANCELLED);
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  async function requestDelete(){
    if(!current)throw new Error('SMOKING_SESSION_NOT_OPEN');
    if(!['draft','planned','cancelled'].includes(current.status)){
      throw new Error('SMOKING_SESSION_DELETE_NOT_ALLOWED');
    }
    return Object.freeze({
      ok:false,
      status:'confirmation_required',
      action:'delete',
      sessionId:current.id
    });
  }

  async function remove({confirmed=false}={}){
    const plan=await requestDelete();
    if(!confirmed)return plan;
    current=await sessionStore.softDelete(current.id);
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  function navigationState(){
    if(!current)return Object.freeze({safeToLeave:true,autosaved:true,sessionId:null});
    return Object.freeze({
      safeToLeave:true,
      autosaved:true,
      sessionId:current.id,
      syncPending:String(current.syncStatus||'').startsWith('pending_')
    });
  }

  return Object.freeze({
    create,resume,resumeOrCreate,open,autosave,setSmoker,clearSmoker,
    plannedReadiness,saveAsPlanned,startSmoking,requestFinish,finish,requestCancel,cancel,requestDelete,remove,
    navigationState,snapshot
  });
}
